import * as React from 'react';

import { enumPurchaseOrderStatus } from '@rematter/frontend-lib-api-client';
import { useToast } from '@rematter/ui-web/hooks';
import { cn } from '@rematter/ui-web/lib';
import { useSetAtom } from 'jotai';
import {
  PlusCircle,
  Printer,
  Trash2,
  Undo2,
  CheckCircle,
  FolderOpen,
} from 'lucide-react';

import { PurchaseOrderRecord } from '../../../types/purchaseOrder';

import { purchaseOrderOpenAtom } from '~/modules/trade/_graphql/mutationPurchaseOrderOpen';
import { purchaseOrderReopenAtom } from '~/modules/trade/_graphql/mutationPurchaseOrderReopen';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';

export type LabelWithIconProps = {
  label: string;
  icon: React.ReactNode;
  className?: string;
};

export const LabelWithIcon = ({
  label,
  icon,
  className,
}: LabelWithIconProps) => (
  <div className={cn('@LabelWithIcon', 'flex items-center gap-2', className)}>
    {icon}
    {label}
  </div>
);

type UsePurchaseOrderDetailMenuItemsProps = {
  purchaseOrder: PurchaseOrderRecord | null;
  setIsLoadDialogOpen?: (open: boolean, purchaseOrderId?: string) => void;
  showVoidButton?: boolean;
  onVoid?: (purchaseOrderId: string) => void;
  onClose?: (purchaseOrderId: string) => void;
  onRefetch?: () => void;
  displayListActions?: boolean;
};

export const usePurchaseOrderDetailMenuItems = ({
  purchaseOrder,
  setIsLoadDialogOpen,
  showVoidButton = false,
  onVoid,
  onClose,
  onRefetch,
  displayListActions = false,
}: UsePurchaseOrderDetailMenuItemsProps) => {
  const toast = useToast();
  const t = useTranslations();
  const openPurchaseOrderMutation = useSetAtom(purchaseOrderOpenAtom({}));
  const reopenPurchaseOrderMutation = useSetAtom(purchaseOrderReopenAtom({}));

  const canCreateLoad = React.useMemo(
    () =>
      purchaseOrder?.status !== enumPurchaseOrderStatus.Draft &&
      purchaseOrder?.status !== enumPurchaseOrderStatus.Voided,
    [purchaseOrder],
  );

  const canOpen = React.useMemo(
    () => purchaseOrder?.status === enumPurchaseOrderStatus.Draft,
    [purchaseOrder],
  );

  const canVoid = React.useMemo(
    () =>
      showVoidButton &&
      purchaseOrder?.status !== enumPurchaseOrderStatus.Closed &&
      purchaseOrder?.status !== enumPurchaseOrderStatus.Voided,
    [purchaseOrder, showVoidButton],
  );

  const canReopen = React.useMemo(
    () =>
      displayListActions &&
      purchaseOrder?.status === enumPurchaseOrderStatus.Closed,
    [purchaseOrder?.status, displayListActions],
  );

  const canRevertFromVoided = React.useMemo(
    () =>
      displayListActions &&
      purchaseOrder?.status === enumPurchaseOrderStatus.Voided,
    [purchaseOrder?.status, displayListActions],
  );

  const canClose = React.useMemo(
    () =>
      displayListActions &&
      purchaseOrder?.status === enumPurchaseOrderStatus.Open,
    [purchaseOrder?.status, displayListActions],
  );

  const handleOpenPurchaseOrder = React.useCallback(async () => {
    if (!purchaseOrder?.id) {
      return;
    }
    try {
      await openPurchaseOrderMutation({ id: purchaseOrder.id });
      toast.success(t.purchaseOrderDetail.messages.opened);
      onRefetch?.();
    } catch (error) {
      toast.error(
        `Failed to open purchase order: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }, [purchaseOrder?.id, openPurchaseOrderMutation, t, toast, onRefetch]);

  const handleCreateLoad = React.useCallback(() => {
    setIsLoadDialogOpen?.(true, purchaseOrder?.id);
  }, [setIsLoadDialogOpen, purchaseOrder?.id]);

  const handlePrintPurchaseOrder = React.useCallback(() => {
    toast.info(t.shared.printComingSoon);
  }, [toast]);

  const handleVoidPurchaseOrder = React.useCallback(() => {
    if (!purchaseOrder?.id) {
      toast.error(t.purchaseOrderDetail.messages.noPurchaseOrderIdToVoid);
      return;
    }
    onVoid?.(purchaseOrder.id);
  }, [purchaseOrder?.id, t, toast, onVoid]);

  const handleReopenPurchaseOrder = React.useCallback(async () => {
    if (!purchaseOrder?.id) {
      toast.error(t.purchaseOrderDetail.messages.noPurchaseOrderIdToReopen);
      return;
    }
    try {
      await reopenPurchaseOrderMutation({ id: purchaseOrder.id });
      toast.success(t.purchaseOrderDetail.messages.reopened);
      onRefetch?.();
    } catch (error) {
      toast.error(
        `Failed to reopen purchase order: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }, [purchaseOrder?.id, reopenPurchaseOrderMutation, t, toast, onRefetch]);

  const handleClosePurchaseOrder = React.useCallback(() => {
    if (!purchaseOrder?.id) {
      toast.error(t.purchaseOrderDetail.messages.noPurchaseOrderIdToClose);
      return;
    }
    onClose?.(purchaseOrder.id);
  }, [purchaseOrder?.id, t, toast, onClose]);

  return React.useMemo(
    () => [
      ...(canOpen
        ? [
            {
              key: 'openPurchaseOrder',
              onAction: handleOpenPurchaseOrder,
              label: (
                <LabelWithIcon
                  label={t.purchaseOrderDetail.actions.open}
                  icon={<FolderOpen className="size-4" />}
                />
              ),
            },
          ]
        : []),
      ...(canCreateLoad
        ? [
            {
              key: 'createLoad',
              onAction: handleCreateLoad,
              label: (
                <LabelWithIcon
                  label={t.purchaseOrderDetail.actions.createLoad}
                  icon={<PlusCircle className="size-4" />}
                />
              ),
            },
          ]
        : []),
      {
        key: 'printPurchaseOrder',
        onAction: handlePrintPurchaseOrder,
        label: (
          <LabelWithIcon
            label={t.purchaseOrderDetail.actions.printPurchaseOrder}
            icon={<Printer className="size-4" />}
          />
        ),
      },
      ...(canReopen || canRevertFromVoided
        ? [
            {
              key: 'reopen',
              onAction: handleReopenPurchaseOrder,
              label: (
                <LabelWithIcon
                  label={t.purchaseOrderDetail.actions.reopen}
                  icon={<Undo2 className="size-4" />}
                />
              ),
            },
          ]
        : []),
      ...(canClose
        ? [
            {
              key: 'closePurchaseOrder',
              onAction: handleClosePurchaseOrder,
              label: (
                <LabelWithIcon
                  label={t.purchaseOrderDetail.actions.closePurchaseOrder}
                  icon={<CheckCircle className="size-4" />}
                />
              ),
            },
          ]
        : []),
      ...(canVoid
        ? [
            {
              key: 'voidPurchaseOrder',
              onAction: handleVoidPurchaseOrder,
              label: (
                <LabelWithIcon
                  className="text-failure"
                  label={t.purchaseOrderDetail.actions.voidPurchaseOrder}
                  icon={<Trash2 className="size-4" />}
                />
              ),
            },
          ]
        : []),
    ],
    [
      t,
      handleOpenPurchaseOrder,
      handleCreateLoad,
      handlePrintPurchaseOrder,
      handleVoidPurchaseOrder,
      handleReopenPurchaseOrder,
      handleClosePurchaseOrder,
      canOpen,
      canCreateLoad,
      canVoid,
      canReopen,
      canRevertFromVoided,
      canClose,
    ],
  );
};
